import { Button } from '@bb/ui';
import type { cardDelivery } from '@bb/contracts';
import { customEventIssues } from '../../campaigns/trigger/actionBased.ts';
import { CustomEventFields } from '../../campaigns/trigger/TriggerEditor.tsx';

/** `CARD_REMOVAL_TRIGGERS_MAX` of the contract. */
export const CARD_REMOVAL_EVENTS_MAX = 10;
type Trigger = cardDelivery.CardRemovalTrigger;

const newTrigger = (): Trigger => ({ kind: 'custom_event', event: '', propertyFilters: [] });

/** Issues of the removal events (`<i>.event`, `<i>.propertyFilters.<j>.<field>`): an event each, valid property filters. */
export const cardRemovalIssues = (triggers: readonly Trigger[]) => triggers.flatMap((t, i) => customEventIssues([i], t.event, t.propertyFilters ?? []));

/** The channel content with these removal events, or without the key when there are none (the contract's optional field). */
export function withRemovalTriggers<T extends Record<string, unknown>>(content: T, triggers: Trigger[]): T {
  const { removalTriggers: _old, ...rest } = content;
  return (triggers.length ? { ...rest, removalTriggers: triggers } : rest) as T;
}

/** The stored value as triggers: anything that is not a list reads as none. */
export const removalTriggersOf = (value: unknown): Trigger[] => (Array.isArray(value) ? (value as Trigger[]).map((t) => ({ ...t, kind: 'custom_event', propertyFilters: t.propertyFilters ?? [] })) : []);

/**
 * Removal events of a Content Card (BB-65, CARD-005): when the user performs one of these custom events — optionally
 * only with matching properties — their card of this message is withdrawn and leaves the feed for good. Up to ten
 * events, each with up to ten property filters (the operators of segment filters, as in action-based triggers). Only
 * events after the user became eligible for the card count. Purchases are not ingested, so there is no purchase event.
 */
export function CardRemovalField({ environmentId, value, onChange, disabled = false }: { environmentId: string; value: unknown; onChange: (triggers: Trigger[]) => void; disabled?: boolean }) {
  const triggers = removalTriggersOf(value);
  const issues = cardRemovalIssues(triggers);
  const set = (next: Trigger[]) => onChange(next);
  return (
    <fieldset aria-label="Removal events" className="card-removal">
      <legend>Removal events</legend>
      <p className="hint">When the user performs one of these events after getting the card, the card is withdrawn from their feed and never comes back.</p>
      {triggers.length === 0 && <p className="hint">No removal events: the card stays until it expires or the user dismisses it.</p>}
      <ol className="plain-list audience-builder">
        {triggers.map((t, i) => {
          const label = `Removal event ${i + 1}`;
          return (
            <li key={i}>
              {i > 0 && (
                <p className="hint" aria-hidden="true">
                  or
                </p>
              )}
              <fieldset className="audience-group" aria-label={label}>
                <legend>{label}</legend>
                <CustomEventFields environmentId={environmentId} readOnly={disabled} issues={issues} path={[i]} spec={t} onChange={(next) => next.kind === 'custom_event' && set(triggers.map((x, k) => (k === i ? { kind: 'custom_event', event: next.event, propertyFilters: next.propertyFilters } : x)))} />
                {!disabled && (
                  <Button size="sm" variant="ghost" aria-label={`Remove ${label.toLowerCase()}`} onClick={() => set(triggers.filter((_, k) => k !== i))}>
                    Remove
                  </Button>
                )}
              </fieldset>
            </li>
          );
        })}
      </ol>
      {!disabled && (
        <Button size="sm" disabled={triggers.length >= CARD_REMOVAL_EVENTS_MAX} onClick={() => set([...triggers, newTrigger()])}>
          Add a removal event
        </Button>
      )}
      {triggers.length >= CARD_REMOVAL_EVENTS_MAX && <p className="hint">A card has at most {CARD_REMOVAL_EVENTS_MAX} removal events.</p>}
    </fieldset>
  );
}
